import './PaymentForm.scss'

import { useCallback, useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  ButtonGroup,
  Col,
  Row,
  Form,
  FloatingLabel,
  Spinner
} from 'react-bootstrap'

import cn from 'classnames'

import RembleLogomark from '../../../assets/images/Remble_Logomark-Green.png'
import { app } from '../../../config'
import {
  KEY_SIGNUP_CODE,
  subscribe,
  verifySignupCode,
  getSubscriptionPlans,
  TRIAL_DURATION,
  DISCOUNT_PERIOD_ONCE,
  DISCOUNT_TYPE_DOLLAR,
  DISCOUNT_TYPE_PERCENT,
  DISCOUNT_TYPE_FIXED_PRICE,
  getPlanPromos,
  startTrial
} from 'components/common/api/Payments'
import {
  saveUserEvent,
  EVENT_CATEGORY_PAYMENTS,
  EVENT_ACTION_COMPLETE,
  EVENT_ACTION_PAGEVIEW,
  trackMetaEvent,
  trackGAEvent
} from 'components/common/api/UserEvents'
import {
  ROUTE_PAYMENTS_COMPLETE,
  ROUTE_TODAY
} from 'components/common/helpers/Paths'
import { navigateTo } from 'components/common/navigation/index'
import CardForm from '../CardForm'
import { fetchAttributionParams } from 'components/common/helpers/Tracking'
import {
  isiOS,
  openUrlInSafari,
  requestInAppSubscriptionPurchase,
  toggleTabBar
} from 'components/common/helpers/Mobile'
import { getAuthenticatedUser } from 'components/auth'
import SubscriptionContext from 'components/common/contexts/SubscriptionContext'

// Marketing promo text for each plan
const planPromos = getPlanPromos()

/**
 * Simple Payment Form component. Used to collect payment information and process PayFactory payment workflows
 */
export default function PaymentForm({
  setReturnPath,
  setHeaderTitle,
  isInline = false,
  onPaymentSuccess = null,
  showTrialToggle = true
}) {
  const { userSubscription } = useContext(SubscriptionContext)
  const user = getAuthenticatedUser()
  const history = useHistory()

  const [plans, setPlans] = useState([])
  const [showCardForm, setShowCardForm] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState()
  const [shouldShowTrial, setShouldShowTrial] = useState(false)
  const [isTrialEnabled, setIsTrialEnabled] = useState(false)

  // Pull out the signup code from localStorage if we saved it earlier
  const [signupCode, setSignupCode] = useState(
    localStorage.getItem(KEY_SIGNUP_CODE) || ''
  )
  const [card, setCard] = useState()
  const [discount, setDiscount] = useState(0)
  const [discountType, setDiscountType] = useState(DISCOUNT_TYPE_PERCENT)
  const [discountPeriod, setDiscountPeriod] = useState(DISCOUNT_PERIOD_ONCE)
  const [trialDuration, setTrialDuration] = useState(0)
  const [isValidSignupCode, setIsValidSignupCode] = useState(false)
  const [isFreeAccount, setIsFreeAccount] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [codeMessage, setCodeMessage] = useState()

  // If the user is using iOS, we have to use the In-App Purchase flow, because Apple doesn't allow us to use the PayFactory flow
  const isInAppPurchase = isiOS()

  /**
   * Helper function to calculate the discounted price
   */
  const discountedPrice = (cost, discount, discountType) => {
    // If we don't have a valid discount, just return the cost
    if (!discount || !discountType) return cost

    // If the discount is a fixed price, just return that
    if (discountType === DISCOUNT_TYPE_FIXED_PRICE) return discount

    // If the discount is a percentage, calculate the discounted price
    // otherwise, just return the cost minus the discount
    return discountType === DISCOUNT_TYPE_PERCENT
      ? cost - (discount / 100) * cost
      : Math.max(0, cost - discount)
  }

  /**
   * Helper function that indicates whether or not to show the discount
   */
  const shouldShowDiscount = () =>
    signupCode && isValidSignupCode && (discount || trialDuration)

  /**
   * Simple helper function to display the discount message
   */
  const discountMessage = () => {
    const prefix = discountType === DISCOUNT_TYPE_DOLLAR ? '$' : ''
    const suffix = discountType === DISCOUNT_TYPE_PERCENT ? '%' : ''
    const duration =
      discountPeriod === DISCOUNT_PERIOD_ONCE ? 'your first' : 'every'

    let discountMessage = ''

    // Do we have a discount?
    if (discount) {
      // Is it a fixed price discount?
      discountMessage =
        discountType === DISCOUNT_TYPE_FIXED_PRICE
          ? `${selectedPlan.period_readable} membership with ${duration} payment for $${discount}`
          : `${prefix}${discount}${suffix} off ${duration} ${selectedPlan.period_readable} payment`
    }

    // Do we have a trial?
    const extendedTrialMessage =
      trialDuration > TRIAL_DURATION
        ? `${discountMessage ? ' and a' : ''} ${trialDuration}-day ${
            discountType !== DISCOUNT_TYPE_FIXED_PRICE ? 'FREE' : ''
          } trial`
        : ''

    return `${discountMessage}${extendedTrialMessage}!`
  }

  /**
   * Helper function to handle updates to card data
   */
  const handleOnChangeCard = useCallback(updatedCard => {
    setCard(updatedCard)
  }, [])

  /**
   * Helper function to handle `blur` on `signup_code` field
   * @param {boolean} isEnterKey - Indicates whether or not the blur event was triggered by the user pressing the Enter key
   * @returns {Promise<Boolean>} - Returns a promise that resolves to a boolean indicating whether or not the code is valid
   */
  const handleSignupCodeBlur = async (isEnterKey = false) => {
    // Don't process for in-app purchases
    if (isInAppPurchase) return

    // If we don't have a selected plan, don't process
    if (!selectedPlan) return

    let codeDiscount = 0,
      finalPrice = selectedPlan.cost,
      isValid = false,
      message = '',
      codeTrialDuration = trialDuration,
      codeDiscountType = discountType,
      codeDiscountPeriod = discountPeriod

    // Only process if we have a code, otherwise clear out the discount
    if (signupCode.trim()) {
      // Check to see if code is valid
      setIsLoading(true)
      try {
        const result = await verifySignupCode(signupCode)
        codeDiscount = result.discount
        isValid = result.valid
        codeDiscountType = result.discount_type
        codeDiscountPeriod = result.discount_period

        // Do we have an extended trial duration?
        if (result.trial_duration) codeTrialDuration = result.trial_duration
        if (result.message) message = result.message
      } catch (error) {} // Swallow exceptions

      // Get final price
      finalPrice = discountedPrice(
        selectedPlan.cost,
        codeDiscount,
        codeDiscountType
      )
    }

    setDiscount(codeDiscount)
    setDiscountType(codeDiscountType)
    setDiscountPeriod(codeDiscountPeriod)
    setTrialDuration(codeTrialDuration)
    setIsValidSignupCode(isValid)
    setCodeMessage(message)

    // Does this user have a "free account" code?
    setIsFreeAccount(isValid && finalPrice === 0)
    setIsLoading(false)

    return isValid
  }

  /**
   * In App Purchase listener initialization
   */
  const initializeListeners = () => {
    new Event('purchaseSuccess')
    new Event('purchaseCancel')
    new Event('purchaseError')

    // Fetch the attribution data we stored earlier
    const attribution = fetchAttributionParams()
    let customData = []

    // If we have a valid signup code, add it to the custom data
    if (isValidSignupCode) customData.push({ signup_code: signupCode })

    // Attempt to add the attribution data to the custom data, if we have it
    try {
      if (attribution) {
        const params = new URLSearchParams(attribution)

        // Add the attribution data to the custom data
        for (const [key, value] of params) customData.push({ [key]: value })
      }
    } catch (error) {} // Swallow exceptions

    // Add listeners to handle the response from the native in-app purchase flow
    window.addEventListener('purchaseSuccess', event => {
      // Subscribe the user
      subscribe({
        plan: selectedPlan,
        in_app_purchase: true,
        signup_code: signupCode,
        customData
      }).then(result => {
        // Did we have a problem?
        if (result?.error) setError(result.error)

        // All good, let's proceed
        if (result?.success) return handlePaymentSuccess(result)

        // We had some kind of error
        if (result?.message) setError(result.message)
        setIsLoading(false)
      })
    })

    window.addEventListener('purchaseCancel', event => {
      setIsLoading(false)
    })

    window.addEventListener('purchaseError', event => {
      setError(
        'There was an error processing your payment. Please try again or contact Remble Support (support@remble.com).'
      )
    })
  }

  /**
   * Helper function to handle "Continue" button click to move to the next step in the flow
   * @param {Object} event Click event
   */
  const handleContinue = async () => {
    // If the user is proceeding with a free trial, we can skip the rest of the form
    if (isTrialEnabled) return submitTrialDetails()

    // If the user is using iOS and on version 1.3.0 or higher, we can use the
    // native in-app purchase flow
    if (isInAppPurchase) {
      setIsLoading(true)

      const subscription = {
        ...selectedPlan,
        signup_code: signupCode
      }

      initializeListeners()

      // Start the in-app purchase flow
      requestInAppSubscriptionPurchase(subscription)
    } else {
      // Do we have a promo code and is invalid?
      if (signupCode && !isValidSignupCode) {
        // Validate the code, in case we missed it on blur
        const valid = await handleSignupCodeBlur()

        // If the code is still invalid, show an error
        if (!valid) return setError('Invalid promo code')
      }

      // Otherwise, we'll use the Payfactory checkout flow
      setShowCardForm(true)
    }
  }

  /**
   * Helper function to handle subscription success
   * @param {Object} payload Successful response payload
   */
  const handlePaymentSuccess = async payload => {
    const label = isTrialEnabled ? 'Trial' : selectedPlan.name
    await saveUserEvent(
      EVENT_CATEGORY_PAYMENTS,
      EVENT_ACTION_COMPLETE,
      label,
      JSON.stringify({ signup_code: signupCode, trial_duration: trialDuration })
    )

    // All set! :)
    // If we have a completion callback, call it, otherwise navigate to the "Complete" page
    if (onPaymentSuccess) onPaymentSuccess(payload)
    else navigateTo(ROUTE_PAYMENTS_COMPLETE, history)
  }

  /**
   * Helper function to submit payment details to our subscription endpoint
   */
  const submitPaymentDetails = async () => {
    setIsLoading(true)
    setError(null)

    // Fetch the attribution data we stored earlier
    const attribution = fetchAttributionParams()
    let customData = []

    // If we have a valid signup code, add it to the custom data
    if (isValidSignupCode) customData.push({ signup_code: signupCode })

    // Attempt to add the attribution data to the custom data, if we have it
    try {
      if (attribution) {
        const params = new URLSearchParams(attribution)

        // Add the attribution data to the custom data
        for (const [key, value] of params) customData.push({ [key]: value })
      }
    } catch (error) {} // Swallow exceptions

    const result = await subscribe({
      plan: selectedPlan,
      card,
      signup_code: signupCode,
      customData
    })

    // Did we have a problem?
    if (result?.error) setError(result.error)

    // All good, let's proceed
    if (result?.success) return handlePaymentSuccess(result)

    // We had some kind of error
    if (result?.message) setError(result.message)
    setIsLoading(false)
  }

  /**
   * Helper function to submit trial details to our trial endpoint
   */
  const submitTrialDetails = async () => {
    setIsLoading(true)
    setError(null)

    // Fetch the attribution data we stored earlier
    const attribution = fetchAttributionParams()
    let customData = []

    // Attempt to add the attribution data to the custom data, if we have it
    try {
      if (attribution) {
        const params = new URLSearchParams(attribution)

        // Add the attribution data to the custom data
        for (const [key, value] of params) customData.push({ [key]: value })
      }
    } catch (error) {} // Swallow exceptions

    const result = await startTrial({
      customData
    })

    // Did we have a problem?
    if (result?.error) setError(result.error)

    // All good, let's proceed
    if (result?.active) return handlePaymentSuccess(result)

    // We had some kind of error
    if (result?.message) setError(result.message)
    setIsLoading(false)
  }

  /**
   * Handle the user toggling the "Enable Trial" switch
   * @param {Object} event The event object
   */
  const handleTrialToggle = event => {
    // Return early if the event is undefined
    if (!event) return

    // Toggle the free trial state
    setIsTrialEnabled(event.currentTarget.checked)
  }

  // Rerender if the subscription changes
  useEffect(() => {
    // Don't show the trial toggle if we don't have a user subscription record
    if (Object.hasOwnProperty.call(userSubscription, 'had_subscription')) {
      // Check to see if the user has had a subscription in the past
      setShouldShowTrial(showTrialToggle && !userSubscription.had_subscription)
    }
  }, [userSubscription, showTrialToggle])

  // Main render
  useEffect(() => {
    // Return early if we've already retrieved the plans
    if (plans.length) return

    // Update header and return path, but only if we're not inlined
    if (!isInline) {
      // Scroll to top
      window.scrollTo(0, 0)

      // If we passed a returnPath in the query string, set it as the return path
      // or default to the Today screen
      const returnPath =
        new URLSearchParams(history.location.search).get('returnPath') ||
        ROUTE_TODAY

      setReturnPath(returnPath)
      setHeaderTitle(null)

      // Hide the tab bar on mobile
      toggleTabBar('off')

      // Track to Meta
      trackMetaEvent('InitiateCheckout', {
        content_name: 'Viewed Plans'
      })

      // Track to GA
      trackGAEvent('begin_checkout', 'begin_checkout', 'Viewed Plans')

      // Log access to the page
      saveUserEvent(EVENT_CATEGORY_PAYMENTS, EVENT_ACTION_PAGEVIEW)
    }

    async function getPlansAsync() {
      setIsLoading(true)

      let subscriptionPlans = await getSubscriptionPlans()

      if (subscriptionPlans.length) {
        // Get our featured selection and in our specified order
        const featuredPlans = app.plans.selection_ids.split(',')

        // Get the actual plan data
        subscriptionPlans = featuredPlans.map(planId => ({
          ...subscriptionPlans.find(
            item => parseInt(item.id) === parseInt(planId)
          )
        }))

        // Add our marketing promo text to the plans
        subscriptionPlans = subscriptionPlans.map(plan => ({
          ...plan,
          ...planPromos.find(item => item.period === plan.period)
        }))

        setPlans(subscriptionPlans)
        setSelectedPlan(subscriptionPlans[0]) // Default to the yearly plan
      }

      setIsLoading(false)
    }
    getPlansAsync()
  }, [isInline, plans, setReturnPath, setHeaderTitle, history])

  return (
    <div
      className={cn('payment-form-container text-center', { inline: isInline })}
    >
      {/* Did we receive an error? */}
      {error && (
        <div className='error mt-2 mb-4'>
          <i className='icon bi-exclamation-circle'></i>{' '}
          <strong>Error: </strong> {' ' + error}
        </div>
      )}

      {/* Hide the plan selection fields when we're displaying the payment form */}
      {!showCardForm && (
        <>
          <Row>
            <Col>
              {/* Don't show logomark if inline */}
              {!isInline && (
                <>
                  <img
                    className='logomark'
                    src={RembleLogomark}
                    alt='Remble'
                    width='80'
                    fetchpriority='high'
                  />
                  <h1 className='title mb-0'>Select a Plan</h1>
                </>
              )}

              {/* Show trial toggle? */}
              {shouldShowTrial && (
                <Form.Group className='form-group visible trial-switch mt-3 enabled'>
                  <Form.Check
                    id='enableTrial'
                    className='text-left'
                    size='lg'
                    label={
                      isTrialEnabled
                        ? 'Start your 7 day free trial'
                        : 'Not ready yet? Start free 7 day trial'
                    }
                    type='switch'
                    onChange={handleTrialToggle}
                    checked={isTrialEnabled}
                    reverse
                  />
                </Form.Group>
              )}

              {/* Don't show the plan selection if the user has opted for a trial */}
              {!isTrialEnabled && (
                <>
                  <ButtonGroup
                    className='subscription-plans'
                    aria-label='Subscription term'
                    vertical
                  >
                    {plans.length > 0 &&
                      plans.map(plan => (
                        <Button
                          key={plan.id}
                          className={cn(plan.sku, {
                            active: selectedPlan?.id === plan.id,
                            recommended: plan.recommendation
                          })}
                          variant='outline-secondary'
                          onClick={() => setSelectedPlan(plan)}
                        >
                          {plan.recommendation && (
                            <span className='recommendation'>
                              {plan.recommendation}
                            </span>
                          )}
                          <span className='plan-name'>{plan.name}</span>
                          {/* Don't show the base price or savings if we don't have a discount */}
                          {plan.discounted_monthly_price && (
                            <>
                              <span className='plan-summary'>
                                {plan.summary}
                              </span>
                              <span className='plan-cost'>${plan.cost}</span>
                            </>
                          )}
                          <span className='monthly-price'>
                            {plan.discounted_monthly_price
                              ? plan.discounted_monthly_price
                              : plan.cost}
                            /month
                          </span>
                        </Button>
                      ))}
                  </ButtonGroup>
                  <h4 className='mb-4'>
                    Your subscription will automatically renew according to your
                    plan
                  </h4>
                  <h5 className='green'>Cancel anytime</h5>
                </>
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              {/* Don't show plan details if user is proceeding with a free trial */}
              {isTrialEnabled ? (
                <div className='my-4 py-2'>
                  <h3 className='green subtitle'>7 Day Free Trial</h3>
                  <p>
                    After your trial expires, your account will be automatically
                    downgraded to the free plan
                  </p>
                </div>
              ) : (
                <div className='plan-details'>
                  {selectedPlan && !isInAppPurchase && (
                    <div className='selection mt-4'>
                      <h2>
                        Your Selection:
                        <span
                          className={`price ${
                            shouldShowDiscount() ? 'discount' : ''
                          }`}
                        >
                          $
                          {discountedPrice(
                            selectedPlan.cost,
                            discount,
                            discountType
                          ).toFixed(2)}
                        </span>
                      </h2>
                      <h4>Paid {selectedPlan.period_readable}</h4>
                    </div>
                  )}

                  {/* Signup code */}
                  {/* Don't show promo code options for In app purchases */}
                  {!isInAppPurchase && (
                    <>
                      <Form.Group
                        className='form-group signup-code mb-5'
                        controlId='formSignupCode'
                      >
                        <FloatingLabel
                          controlId='floatingSignupCode'
                          label='Promo code (optional)'
                        >
                          <Form.Control
                            name='signupCode'
                            size='lg'
                            placeholder='Promo code (optional)'
                            defaultValue={signupCode}
                            type='text'
                            onChange={e => {
                              setSignupCode(e.currentTarget.value)
                              setCodeMessage('') // Clear any error messages
                            }}
                            onBlur={_ => handleSignupCodeBlur()}
                            onKeyDown={e =>
                              e.key === 'Enter' && handleSignupCodeBlur(true)
                            }
                            isValid={signupCode && isValidSignupCode}
                          />
                        </FloatingLabel>
                      </Form.Group>

                      {/* Do we have a discount message? */}
                      {shouldShowDiscount() ? (
                        <div className='discount my-3 subtitle'>
                          {discountMessage()}
                        </div>
                      ) : (
                        // No discount, but do we have an error?
                        signupCode &&
                        codeMessage && (
                          <div className='error my-3'>
                            <strong>{codeMessage}</strong>
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </>
      )}

      {/* Did the user have a signup code for a "free" account? */}
      {showCardForm && isFreeAccount && (
        <Row>
          <Col>
            <h3 className='mt-5 title green'>
              Welcome to Remble {user?.display_name}
            </h3>
            <p className='subtitle mb-5'>
              Your signup code gives you access to a{' '}
              <strong className='green'>FREE</strong> account 🙂
            </p>
          </Col>
        </Row>
      )}

      {/* Don't show the credit card form if we have support for In-App Purchases */}
      {showCardForm && !isFreeAccount && !isInAppPurchase && (
        <>
          {/* Don't show card header if inline */}
          {!isInline && (
            <Row>
              <Col className='text-left'>
                <img
                  src={RembleLogomark}
                  alt='Remble'
                  width='25'
                  fetchpriority='high'
                />
                <h1 className='mt-4 title'>Payment Details</h1>
                <p className='subtitle'>Credit card information</p>
              </Col>
            </Row>
          )}

          <CardForm onChange={handleOnChangeCard} />
        </>
      )}

      <Row>
        {showCardForm && !isInAppPurchase ? (
          <>
            <Col>
              <Button
                size='lg'
                variant='primary-outline'
                className='w-100'
                onClick={() => setShowCardForm(false)}
                disabled={isLoading}
              >
                <span>Back</span>
              </Button>
            </Col>
            <Col>
              <Button
                size='lg'
                variant='primary'
                className='w-100'
                onClick={() => submitPaymentDetails()}
                disabled={
                  isLoading || (!isFreeAccount && card && !card.isValid)
                }
              >
                <span>Subscribe</span>
              </Button>
            </Col>
          </>
        ) : (
          <>
            {/* If the user is on iOS, but doesn't have the version that meets IAP requirements, notify them */}
            {isInAppPurchase && !isiOS('1.3.0') ? (
              <Col>
                <h4 className='text-center green'>
                  Please update your app to the latest version to subscribe.
                </h4>
                <Button
                  variant='primary'
                  size='lg'
                  className='w-100'
                  onClick={() =>
                    openUrlInSafari(
                      'https://apps.apple.com/us/app/remble-relationship-health/id1597957847'
                    )
                  }
                >
                  Update Now
                </Button>
              </Col>
            ) : (
              <Button
                variant='primary'
                size='lg'
                type='submit'
                disabled={isLoading || !selectedPlan}
                className='w-100'
                onClick={handleContinue}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                    />
                    <span className='px-2'>Loading...</span>
                  </>
                ) : isInAppPurchase ? (
                  <span>Subscribe</span>
                ) : (
                  <span>Continue</span>
                )}
              </Button>
            )}
          </>
        )}
      </Row>
    </div>
  )
}
